import React, { useEffect, useState } from 'react'

import { useForm } from 'antd/lib/form/Form'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { DraggableModal } from '../../../../components/DraggableModal'
import { Col, Form, Input, Row, Select } from 'antd'
import { EntStock } from '../../../../models/chemicalization/stock'
import SelectCompanyWarehouses from '../../../../components/selects/SelectCompanyWarehouses'
import SelectTypesFertilizers from '../../../../components/selects/SelectTypesFertilizers'
import SelectCompany from '../../../../components/selects/SelectCompany'

enum FormField {
	id,
	companyId,
	warehouseId,
	fertilizerId,
	pricePerKg,
	availableQuantity,
	distributedQuantity,
	reportingYear,
}

interface IProps {
	show: boolean
	stock: EntStock
	onHide: () => void
	onSubmit: (pos: EntStock) => Promise<boolean>
}

export const AddStockModal = (props: IProps) => {
	const { t } = useTranslation()
	const [form] = useForm()
	const [loading, setLoading] = useState<boolean>(false)
	const [selectedYear, setSelectedYear] = useState<string[]>([])
	const [selectedCompany, setSelectedCompany] = useState<number | null>(null)
	const [selectedCompanyWarehouses, setSelectedWarehouses] = useState<number | null>(null)

	const years = Array.from({ length: 20 }, (_, i) => (new Date().getFullYear() - i).toString())

	useEffect(() => {
		if (props.show) {
			setSelectedCompany(props.stock.warehouseId ?? null)
			setFieldsFromEntity()
		} else {
			setSelectedCompany(null)
		}
	}, [props.show])

	const setFieldsFromEntity = (): void => {
		form.setFields([
			{ name: FormField.companyId, value: props.stock.company?.id },
			{ name: FormField.warehouseId, value: props.stock.warehouseId },
			{ name: FormField.fertilizerId, value: props.stock.fertilizer?.id },
			{ name: FormField.pricePerKg, value: props.stock.pricePerKg },
			{ name: FormField.availableQuantity, value: props.stock.availableQuantity },
			{ name: FormField.distributedQuantity, value: props.stock.distributedQuantity },
			{ name: FormField.reportingYear, value: props.stock.reportingYear },
		])
	}

	const setEntityFromFields = (): EntStock => {
		let res = _.cloneDeep(props.stock)
		res.companyId = form.getFieldValue(FormField.companyId)
		res.warehouseId = form.getFieldValue(FormField.warehouseId)
		res.fertilizerId = form.getFieldValue(FormField.fertilizerId)
		res.pricePerKg = Number(form.getFieldValue(FormField.pricePerKg))
		res.availableQuantity = Number(form.getFieldValue(FormField.availableQuantity))
		res.distributedQuantity = Number(form.getFieldValue(FormField.distributedQuantity))
		res.reportingYear = Number(form.getFieldValue(FormField.reportingYear))
		return res
	}

	const handleSubmit = async () => {
		setLoading(true)
		try {
			await form.validateFields()
			const data = setEntityFromFields()
			const result = await props.onSubmit(data)
			if (result) props.onHide()
		} finally {
			setLoading(false)
		}
	}

	return (
		<>
			<DraggableModal
				title={props.stock.id === null ? t('general.add') : t('general.edit')}
				visible={props.show}
				onCancel={props.onHide}
				onSubmit={handleSubmit}
				submitLoading={loading}
				width={600}
			>
				<Form initialValues={{ remember: true }} autoComplete='off' layout='vertical' form={form}>
					<Row style={{ margin: 0 }} gutter={16}>
						<Col className='gutter-row' span={12}>
							<Form.Item
								label={t('titles.company')}
								name={FormField.companyId}
								rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
							>
								<SelectCompany
									value={selectedCompany}
									onChange={id => {
										setSelectedCompany(id)
										setSelectedWarehouses(null)
										form.setFields([{ name: FormField.warehouseId, value: null }])
									}}
								/>
							</Form.Item>
							<Form.Item
								label={t('titles.company_warehouses')}
								name={FormField.warehouseId}
								rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
							>
								<SelectCompanyWarehouses
									companyId={selectedCompany !== null ? selectedCompany : null}
								/>
							</Form.Item>
							<Form.Item
								label={t('titles.types_of_fertilizers')}
								name={FormField.fertilizerId}
								rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
							>
								<SelectTypesFertilizers />
							</Form.Item>
							<Form.Item
								label={t('chemicalization.stock.price_per_kg')}
								name={FormField.pricePerKg}
								rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
							>
								<Input type='number' />
							</Form.Item>
						</Col>
						<Col className='gutter-row' span={12}>
							<Form.Item
								label={t('chemicalization.stock.available_quantity')}
								name={FormField.availableQuantity}
								rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
							>
								<Input type='number' />
							</Form.Item>
							<Form.Item
								label={t('chemicalization.stock.distributed_quantity')}
								name={FormField.distributedQuantity}
								rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
							>
								<Input type='number' />
							</Form.Item>
							<Form.Item
								label={t('chemicalization.stock.reporting_year')}
								name={FormField.reportingYear}
								rules={[{ required: true, message: t('msg.fill_in_the_field') }]}
							>
								<Select
									value={selectedYear}
									onChange={setSelectedYear}
									placeholder={t('reports.select_years')}
								>
									{years.map(year => (
										<Select.Option key={year} value={year}>
											{year}
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</DraggableModal>
		</>
	)
}
